import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import './GeneralInfoForm.css';
import { CompressImageFile } from '../compressFile/CompressImageFile'
import { toast } from 'react-toastify';

const Documents = forwardRef(({ documents, setDocuments, editMode = false, uploadedDocuments = [], onDocumentDelete }, ref) => {
     // Add validation support
     const [validationErrors, setValidationErrors] = useState({});
     const [deletingDocuments, setDeletingDocuments] = useState(new Set());

     const getDefaultRows = () => {
          if (editMode && Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0) {
               // In edit mode with existing documents, provide empty rows for new uploads
               return [
                    { document_name: '', file: null, preview: null, loading: false, message: '' },
                    { document_name: '', file: null, preview: null, loading: false, message: '' }
               ];
          } else {
               // Default rows for add mode or edit mode without existing documents
               return [
                    { document_name: 'Aadhar Card', file: null, preview: null, loading: false, message: '' },
                    { document_name: 'PAN Card', file: null, preview: null, loading: false, message: '' }
               ];
          }
     };

     const [rows, setRows] = useState(() => {
          // Initialize rows based on mode and existing data
          if (editMode) {
               return getDefaultRows();
          } else if (Array.isArray(documents) && documents.length > 0) {
               return documents.map(doc => ({
                    document_name: doc.document_name || '',
                    file: null,
                    preview: null,
                    loading: false,
                    message: '',
                    isExisting: true
               }));
          } else if (documents.files && documents.files.length > 0) {
               return documents.files;
          } else {
               return getDefaultRows();
          }
     });

     useImperativeHandle(ref, () => ({
          validateForm: () => {
               return true;
          },
          clearValidation: () => {
               setValidationErrors({});
          }
     }));

     const createdObjectUrls = useRef(new Set());

     // Function to delete uploaded document
     const deleteDocument = async (documentId, documentPath) => {
          const token = sessionStorage.getItem('token') || localStorage.getItem('authToken');
          if (!token) {
               toast.error('Authentication token not found. Please login again.');
               return;
          }

          // Add to deleting set to show loading state
          setDeletingDocuments(prev => new Set(prev).add(documentId));

          try {
               const response = await fetch(`${process.env.REACT_APP_BASE_URL}/deleteDocument/${documentId}`, {
                    method: 'DELETE',
                    headers: {
                         'Authorization': `Bearer ${token}`,
                         'Content-Type': 'application/json'
                    }
               });

               const result = await response.json();

               if (!response.ok || !result.status) {
                    toast.error(result.message || 'Failed to delete document');
                    return;
               }

               toast.success('Document deleted successfully');

               // Call callback to refresh uploaded documents if provided
               if (onDocumentDelete) {
                    onDocumentDelete(documentId);
               }

          } catch (error) {
               console.error('Error deleting document:', error);
               toast.error('Network error. Please try again.');
          } finally {
               // Remove from deleting set
               setDeletingDocuments(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(documentId);
                    return newSet;
               });
          }
     };

     // Cleanup object URLs on unmount
     useEffect(() => {
          return () => {
               const urlsToCleanup = Array.from(createdObjectUrls.current);
               urlsToCleanup.forEach(url => {
                    try { URL.revokeObjectURL(url); } catch (e) { }
               });
               createdObjectUrls.current.clear();
          };
     }, []);

     const sanitizeFileName = (name) => {
          return name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
     };

     const handleChange = (index, field, value) => {
          const updated = rows.map((r, i) => (i === index ? { ...r, [field]: value } : r));
          setRows(updated);

          // Update parent component
          setDocuments({
               files: updated,
               info: updated.filter(r => r.document_name && r.file).map(doc => ({
                    document_name: doc.document_name,
                    file_type: doc.file?.type,
                    file_size: doc.file?.size,
                    original_name: doc.file?.name
               }))
          });
     };

     const addRow = () => {
          const updated = [...rows, { document_name: '', file: null, preview: null, loading: false, message: '' }];
          setRows(updated);
          setDocuments({
               files: updated,
               info: updated.filter(r => r.document_name && r.file).map(doc => ({
                    document_name: doc.document_name,
                    file_type: doc.file?.type,
                    file_size: doc.file?.size,
                    original_name: doc.file?.name
               }))
          });
     };

     const removeRow = (index) => {
          if (rows.length <= 2) return;
          const target = rows[index];
          if (target && target.preview && target.__isObjectUrl) {
               try { URL.revokeObjectURL(target.preview); createdObjectUrls.current.delete(target.preview); } catch (e) { /* ignore */ }
          }
          const updated = rows.filter((_, i) => i !== index);
          setRows(updated);
          setDocuments({
               files: updated,
               info: updated.filter(r => r.document_name && r.file).map(doc => ({
                    document_name: doc.document_name,
                    file_type: doc.file?.type,
                    file_size: doc.file?.size,
                    original_name: doc.file?.name
               }))
          });
     };

     // Replace the handleFileInput function with this improved version:

     const handleFileInput = async (index, files) => {
          const file = files[0];
          if (!file) return;

          console.log('Starting file processing for:', file.name, 'Type:', file.type, 'Size:', file.size);

          // Set initial loading state
          let updated = rows.map((r, i) => i === index ? { ...r, loading: true, message: 'Processing...', file: null, preview: null } : r);
          setRows(updated);
          setDocuments({
               files: updated,
               info: updated.filter(r => r.document_name && r.file).map(doc => ({
                    document_name: doc.document_name,
                    file_type: doc.file?.type,
                    file_size: doc.file?.size,
                    original_name: doc.file?.name
               }))
          });

          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
          const MAX_SIZE_MB = 2;

          try {
               const fileType = file.type;
               let fileSize = file.size / 1024 / 1024;

               console.log('File validation - Type:', fileType, 'Size (MB):', fileSize);

               // Validate file type
               if (!validTypes.includes(fileType)) {
                    console.log('Invalid file type');
                    updated = rows.map((r, i) => i === index ? {
                         ...r,
                         file: null,
                         preview: null,
                         loading: false,
                         message: 'Only JPG, PNG or PDF allowed'
                    } : r);
                    setRows(updated);
                    setDocuments({
                         files: updated,
                         info: updated.filter(r => r.document_name && r.file).map(doc => ({
                              document_name: doc.document_name,
                              file_type: doc.file?.type,
                              file_size: doc.file?.size,
                              original_name: doc.file?.name
                         }))
                    });
                    return;
               }

               // Handle PDF files
               if (fileType === 'application/pdf') {
                    console.log('Processing PDF file');

                    if (fileSize > MAX_SIZE_MB) {
                         console.log('PDF too large:', fileSize, 'MB');
                         try {
                              const inputEl = document.getElementById(`doc_file_${index}`);
                              if (inputEl) inputEl.value = '';
                         } catch (e) { console.error('Error clearing input:', e); }

                         updated = rows.map((r, i) => i === index ? {
                              ...r,
                              file: null,
                              preview: null,
                              loading: false,
                              message: `PDF exceeds ${MAX_SIZE_MB}MB`
                         } : r);
                         setRows(updated);
                         setDocuments({
                              files: updated,
                              info: updated.filter(r => r.document_name && r.file).map(doc => ({
                                   document_name: doc.document_name,
                                   file_type: doc.file?.type,
                                   file_size: doc.file?.size,
                                   original_name: doc.file?.name
                              }))
                         });
                         return;
                    }

                    // Validate PDF header
                    console.log('Validating PDF header...');
                    const arrayBuffer = await file.arrayBuffer();
                    const uint8 = new Uint8Array(arrayBuffer);
                    const header = [0x25, 0x50, 0x44, 0x46]; // %PDF
                    const isPdf = header.every((b, idx) => uint8[idx] === b);

                    if (!isPdf) {
                         console.log('Invalid PDF header');
                         updated = rows.map((r, i) => i === index ? {
                              ...r,
                              file: null,
                              preview: null,
                              loading: false,
                              message: 'Invalid or corrupted PDF'
                         } : r);
                         setRows(updated);
                         setDocuments({
                              files: updated,
                              info: updated.filter(r => r.document_name && r.file).map(doc => ({
                                   document_name: doc.document_name,
                                   file_type: doc.file?.type,
                                   file_size: doc.file?.size,
                                   original_name: doc.file?.name
                              }))
                         });
                         return;
                    }

                    console.log('PDF validation successful, creating file...');
                    const safeName = `${Date.now()}_${sanitizeFileName(file.name)}`;
                    const finalFile = new File([file], safeName, { type: file.type });
                    // Create a better PDF icon
                    const pdfIcon = 'data:image/svg+xml;base64,' + btoa(`
<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
  <polyline points="14,2 14,8 20,8"/>
  <text x="12" y="18" font-family="Arial, sans-serif" font-size="4" text-anchor="middle" fill="#dc3545">PDF</text>
</svg>
`);
                    updated = rows.map((r, i) => i === index ? {
                         ...r,
                         file: finalFile,
                         preview: pdfIcon,
                         __isObjectUrl: false,
                         loading: false,
                         message: 'PDF processed successfully'
                    } : r);
                    setRows(updated);
                    setDocuments({
                         files: updated,
                         info: updated.filter(r => r.document_name && r.file).map(doc => ({
                              document_name: doc.document_name,
                              file_type: doc.file?.type,
                              file_size: doc.file?.size,
                              original_name: doc.file?.name
                         }))
                    });
                    console.log('PDF processing completed');
                    return;
               }

               // Handle image files
               console.log('Processing image file');
               let uploadFile = file;
               let compressionAttempts = 0;
               const MAX_COMPRESSION_ATTEMPTS = 3;

               if (fileSize > MAX_SIZE_MB) {
                    console.log('Image needs compression, current size:', fileSize, 'MB');

                    while (fileSize > MAX_SIZE_MB && compressionAttempts < MAX_COMPRESSION_ATTEMPTS) {
                         compressionAttempts++;
                         console.log(`Compression attempt ${compressionAttempts}...`);

                         // Update message to show compression progress
                         updated = rows.map((r, i) => i === index ? {
                              ...r,
                              loading: true,
                              message: `Compressing... (${compressionAttempts}/${MAX_COMPRESSION_ATTEMPTS})`
                         } : r);
                         setRows(updated);

                         const targetSize = MAX_SIZE_MB - (0.2 * compressionAttempts);
                         console.log('Target size for compression:', targetSize, 'MB');

                         try {
                              uploadFile = await CompressImageFile(uploadFile, targetSize, `DOC_attempt${compressionAttempts}`);
                              fileSize = uploadFile.size / 1024 / 1024;
                              console.log('After compression attempt', compressionAttempts, '- New size:', fileSize, 'MB');
                         } catch (compressionError) {
                              console.error('Compression failed:', compressionError);
                              break;
                         }
                    }

                    if (fileSize > MAX_SIZE_MB) {
                         console.log('Compression failed, final size:', fileSize, 'MB');
                         try {
                              const inputEl = document.getElementById(`doc_file_${index}`);
                              if (inputEl) inputEl.value = '';
                         } catch (e) { console.error('Error clearing input:', e); }

                         updated = rows.map((r, i) => i === index ? {
                              ...r,
                              file: null,
                              preview: null,
                              loading: false,
                              message: `Image too large after compression (${fileSize.toFixed(1)}MB)`
                         } : r);
                         setRows(updated);
                         setDocuments({
                              files: updated,
                              info: updated.filter(r => r.document_name && r.file).map(doc => ({
                                   document_name: doc.document_name,
                                   file_type: doc.file?.type,
                                   file_size: doc.file?.size,
                                   original_name: doc.file?.name
                              }))
                         });
                         return;
                    }
               }

               console.log('Creating final processed file...');
               const safeName = `${Date.now()}_${sanitizeFileName(uploadFile.name)}`;
               const processedFile = new File([uploadFile], safeName, { type: uploadFile.type });
               const previewUrl = URL.createObjectURL(processedFile);
               createdObjectUrls.current.add(previewUrl);

               updated = rows.map((r, i) => i === index ? {
                    ...r,
                    file: processedFile,
                    preview: previewUrl,
                    __isObjectUrl: true,
                    loading: false,
                    message: compressionAttempts > 0 ? `Compressed successfully (${fileSize.toFixed(1)}MB)` : 'Image processed successfully'
               } : r);
               setRows(updated);
               setDocuments({
                    files: updated,
                    info: updated.filter(r => r.document_name && r.file).map(doc => ({
                         document_name: doc.document_name,
                         file_type: doc.file?.type,
                         file_size: doc.file?.size,
                         original_name: doc.file?.name
                    }))
               });
               console.log('Image processing completed successfully');

          } catch (err) {
               console.error('Error processing file:', err);
               updated = rows.map((r, i) => i === index ? {
                    ...r,
                    file: null,
                    preview: null,
                    loading: false,
                    message: `Failed to process file: ${err.message || 'Unknown error'}`
               } : r);
               setRows(updated);
               setDocuments({
                    files: updated,
                    info: updated.filter(r => r.document_name && r.file).map(doc => ({
                         document_name: doc.document_name,
                         file_type: doc.file?.type,
                         file_size: doc.file?.size,
                         original_name: doc.file?.name
                    }))
               });
          }
     };
     // Update the openDocument function:
     const openDocument = (documentPath) => {
          if (!documentPath) return;

          // Extract filename from the path
          const filename = documentPath.split('/').pop();

          // Use the API route structure
          const documentUrl = `${process.env.REACT_APP_BASE_URL}/employee-document/${filename}`;

          window.open(documentUrl, '_blank', 'noopener,noreferrer');
     };

     // Update the getThumbnail function:
     const getThumbnail = (documentPath) => {
          if (!documentPath) return null;

          const isImage = /\.(jpg|jpeg|png|gif)$/i.test(documentPath);
          const filename = documentPath.split('/').pop();

          if (isImage) {
               // For images, return the API employee-document URL
               return `${process.env.REACT_APP_BASE_URL}/employee-document/${filename}`;
          } else {
               // PDF icon as SVG for non-images
               return 'data:image/svg+xml;utf8,' + encodeURIComponent(`
               <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%23d44638' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'>
                    <rect x='3' y='3' width='18' height='18' rx='2' ry='2'/>
                    <path d='M7 7h6v6'/>
                    <text x='12' y='18' font-size='6' text-anchor='middle' fill='%23d44638'>PDF</text>
               </svg>
          `);
          }
     };

     return (
          <div className="custom_form_wrapper">
               {/* Show uploaded documents table in edit mode */}
               {editMode && Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0 && (
                    <div className="mb-4">
                         <h6 className="mb-3 text-muted">Uploaded Documents</h6>
                         <div className="table-responsive">
                              <table className="table table-sm table-bordered">

                                   <tbody>
                                        {uploadedDocuments.map((doc, idx) => (
                                             <tr key={idx}>
                                                  <td className="align-middle">{doc.document_name}</td>
                                                  <td className="align-middle">
                                                       <span
                                                            style={{
                                                                 cursor: 'pointer',
                                                                 color: '#0066cc',
                                                                 textDecoration: 'underline',
                                                                 wordBreak: 'break-all',
                                                                 fontSize: '0.9rem'
                                                            }}
                                                            onClick={() => openDocument(doc.document_path)}
                                                            title="Click to open file"
                                                       >
                                                            {doc.document_path ?
                                                                 // Clean up the filename display - remove timestamp prefix if present
                                                                 doc.document_path.split('/').pop().replace(/^\d+_/, '')
                                                                 : 'No file'
                                                            }
                                                       </span>
                                                  </td>

                                                  <td className="align-middle text-center">
                                                       <div
                                                            style={{
                                                                 cursor: 'pointer',
                                                                 display: 'inline-block'
                                                            }}
                                                            onClick={() => openDocument(doc.document_path)}
                                                            title="Click to open file"
                                                       >
                                                            {/\.(jpg|jpeg|png|gif)$/i.test(doc.document_path) ? (
                                                                 <img
                                                                      src={getThumbnail(doc.document_path)}
                                                                      alt="Document thumbnail"
                                                                      style={{
                                                                           width: 40,
                                                                           height: 40,
                                                                           objectFit: 'cover',
                                                                           borderRadius: 4,
                                                                           border: '1px solid #ddd'
                                                                      }}
                                                                      onError={(e) => {
                                                                           e.target.style.display = 'none';
                                                                           e.target.nextSibling.style.display = 'inline-block';
                                                                      }}
                                                                 />
                                                            ) : (
                                                                 // Use Font Awesome icon for PDF instead of SVG
                                                                 <div
                                                                      style={{
                                                                           width: 40,
                                                                           height: 40,
                                                                           display: 'flex',
                                                                           alignItems: 'center',
                                                                           justifyContent: 'center',
                                                                           backgroundColor: '#f8f9fa',
                                                                           border: '1px solid #dc3545',
                                                                           borderRadius: 4,
                                                                           color: '#dc3545'
                                                                      }}
                                                                 >
                                                                      <i className="fas fa-file-pdf" style={{ fontSize: '20px' }}></i>
                                                                 </div>
                                                            )}
                                                            <span
                                                                 style={{
                                                                      display: 'none',
                                                                      fontSize: '0.8rem',
                                                                      color: '#666',
                                                                      padding: '8px 12px',
                                                                      border: '1px solid #ddd',
                                                                      borderRadius: 4,
                                                                      backgroundColor: '#f8f9fa'
                                                                 }}
                                                            >
                                                                 File
                                                            </span>
                                                       </div>
                                                  </td>
                                                  <td className="align-middle text-center">
                                                       <div className="d-flex justify-content-center gap-2">
                                                            <button
                                                                 type="button"
                                                                 className="btn btn-outline-primary btn-sm"
                                                                 onClick={() => openDocument(doc.document_path)}
                                                                 title="View Document"
                                                                 style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                            >
                                                                 <i className="fas fa-eye"></i>
                                                            </button>
                                                            <button
                                                                 type="button"
                                                                 className="btn btn-outline-danger btn-sm"
                                                                 onClick={() => deleteDocument(doc.document_id || idx, doc.document_path)}
                                                                 disabled={deletingDocuments.has(doc.document_id || idx)}
                                                                 title="Delete Document"
                                                                 style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                            >
                                                                 {deletingDocuments.has(doc.document_id || idx) ? (
                                                                      <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px' }}></span>
                                                                 ) : (
                                                                      <i className="fas fa-trash"></i>
                                                                 )}
                                                            </button>
                                                       </div>
                                                  </td>
                                             </tr>
                                        ))}
                                   </tbody>
                              </table>
                         </div>
                         <hr className="my-3" />
                    </div>
               )}

               {/* Upload new documents section */}



               <div className="experience-table-body row">
                    <div className="col-sm-11">
                         {rows.map((row, idx) => (
                              <div key={idx} className="experience-table-row mb-2">
                                   <div className="row gx-2 align-items-center">
                                        <div className="col-5">
                                             <div className="form-floating">
                                                  <input
                                                       type="text"
                                                       className="form-control form-control-sm"
                                                       placeholder="Document name"
                                                       value={row.document_name}
                                                       onChange={e => handleChange(idx, 'document_name', e.target.value)}
                                                       id={`doc_name_${idx}`}
                                                  />
                                                  <label htmlFor={`doc_name_${idx}`}>Document name</label>
                                             </div>
                                        </div>



                                        <div className="col-5">
                                             <div className="d-flex align-items-start">
                                                  <div style={{ flex: 1 }}>
                                                       <div className="form-floating">
                                                            <input
                                                                 type="file"
                                                                 className="form-control form-control-sm"
                                                                 onChange={e => handleFileInput(idx, e.target.files)}
                                                                 id={`doc_file_${idx}`}
                                                                 accept=".jpg,.jpeg,.png,.pdf"
                                                            />
                                                            <label htmlFor={`doc_file_${idx}`} style={{ opacity: row.file ? 1 : 0 }}>
                                                                 {row.file ? row.file.name : 'Upload file'}
                                                            </label>
                                                       </div>
                                                       {/* {row.loading && (
                                                       <div className="small text-muted mt-1">
                                                            <span className="spinner-border spinner-border-sm text-primary me-1" />
                                                            Processing...
                                                       </div>
                                                  )} */}

                                                  </div>



                                                  <div style={{ marginLeft: 12, minWidth: 85 }} className="d-flex flex-column align-items-center">
                                                       {/* Thumbnail */}
                                                       <div style={{
                                                            width: 56,
                                                            height: 40,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginBottom: '2px'
                                                       }}>
                                                            {row.preview ? (
                                                                 row.file && row.file.type === 'application/pdf' ? (
                                                                      <div
                                                                           style={{
                                                                                width: 36,
                                                                                height: 36,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                backgroundColor: '#f8f9fa',
                                                                                border: '1px solid #dc3545',
                                                                                borderRadius: 4
                                                                           }}
                                                                      >
                                                                           <img
                                                                                src={row.preview}
                                                                                alt="pdf"
                                                                                style={{ width: 32, height: 32 }}
                                                                                onError={(e) => {
                                                                                     // Fallback if SVG doesn't load
                                                                                     e.target.style.display = 'none';
                                                                                     e.target.parentElement.innerHTML = '<span style="color: #dc3545; font-size: 10px; font-weight: bold;">PDF</span>';
                                                                                }}
                                                                           />
                                                                      </div>
                                                                 ) : (
                                                                      <img
                                                                           src={row.preview}
                                                                           alt="preview"
                                                                           style={{
                                                                                maxWidth: 48,
                                                                                maxHeight: 40,
                                                                                objectFit: 'cover',
                                                                                borderRadius: 4,
                                                                                border: '1px solid #ddd'
                                                                           }}
                                                                      />
                                                                 )
                                                            ) : (
                                                                 <></>
                                                            )}
                                                       </div>

                                                  </div>
                                                  <div>
                                                       {/* Show error messages below input */}
                                                       {row.message && !row.loading && !row.file && (
                                                            <div className="small text-danger mt-1">{row.message}</div>
                                                       )}
                                                       {/* Success message below thumbnail */}
                                                       {row.message && !row.loading && row.file && (
                                                            <div
                                                                 className="small text-success"
                                                                 style={{
                                                                      fontSize: '0.6rem',
                                                                      textAlign: 'center',
                                                                      verticalAlign: 'middle',
                                                                      lineHeight: '1.0',
                                                                      wordBreak: 'break-word',
                                                                      fontWeight: '500',
                                                                      color: '#28a745'
                                                                 }}
                                                            >
                                                                 {row.message}
                                                            </div>
                                                       )}
                                                  </div>
                                             </div>
                                        </div>
                                        <div className="col-1 text-center">
                                             {rows.length > 2 && (
                                                  <button type="button" className="btn btn-outline-danger btn-sm"
                                                       onClick={() => removeRow(idx)}
                                                       style={{
                                                            fontSize: '0.75rem', width: '30px',
                                                            height: '30px',
                                                            minWidth: '30px',
                                                            minHeight: '30px',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                       }} title="Remove Document">
                                                       <i className="fas fa-trash" />
                                                  </button>
                                             )}
                                        </div>
                                   </div>
                              </div>
                         ))}
                    </div>
                    <div className='col-sm-1 float-end'>
                         <button type="button" style={{
                              fontSize: '0.8rem',
                              width: '30px',
                              height: '30px',
                              minWidth: '30px',
                              minHeight: '30px',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                         }} className="btn btn-primary btn-sm" onClick={addRow} >
                              <i className="fas fa-plus me-1"></i>
                         </button>
                    </div>
               </div>
          </div>
     );
});

export default Documents;